import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { QuizLayout } from '../components/QuizLayout';
import { StripeCheckoutPanel } from '../components/StripeCheckoutPanel';
import { PLANS, CHECKOUT_FEATURES, type Plan } from '../data/plans';
import { REVIEWS, type Review } from '../data/reviews';
import { rankReviewsByPersona } from '../lib/reviewRanking';
import { useQuizStore } from '../store/quizStore';
import { matchCoach, type MatchedCoach } from '../lib/coachMatch';
import { postJson } from '../lib/api';
import { track } from '../lib/analytics';

interface CheckoutResp {
  ok: boolean;
  redirectUrl?: string;
  error?: string;
}

interface AddOn {
  /** 'recruiter' if user is actively job-seeking; 'coach' if growing in role. */
  flavor: 'recruiter' | 'coach';
  /** Cents — $49 (30-min) or $67 (60-min, executive) depending on salary band. */
  amount: number;
  /** Short question for the gate header. */
  gateTitle: string;
  /** One-line description for the gate body. */
  gateBlurb: string;
  /** The specific coach we matched them with (drives the gate UI + booking). */
  coach: MatchedCoach;
}

export function Checkout() {
  const { plan: planId } = useParams<{ plan: string }>();
  const navigate = useNavigate();
  const sessionId = useQuizStore((s) => s.sessionId);
  const setStorePlan = useQuizStore((s) => s.setPlan);
  const variables = useQuizStore((s) => s.variables);

  // Support variant suffixes like "monthly-2" for backward-compat with any
  // bookmarked test URLs — strip the suffix to find the base plan. All
  // variants now use the same /api/create-checkout endpoint; the v2 split
  // was rolled into the default behaviour.
  const basePlanId = planId?.replace(/-\d+$/, '') ?? '';
  const plan = PLANS.find((p) => p.id === planId || p.id === basePlanId) ?? null;
  const leadEmailForMatch = useQuizStore((s) => s.leadEmail);
  const addOn = useMemo(() => deriveAddOn(variables, leadEmailForMatch), [variables, leadEmailForMatch]);

  // Default 'no' so checkout is immediately usable; user can flip to 'yes'
  // via the toggle that sits above the checkout panel.
  const [addOnChoice, setAddOnChoice] = useState<'yes' | 'no'>('no');
  const includeAddOn = addOnChoice === 'yes';
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (plan) {
      track('checkout_viewed', { plan: plan.id });
      setStorePlan(plan.id);
    }
  }, [plan, setStorePlan]);

  if (!plan) return <Navigate to="/pricing" replace />;

  const planDollars = parseFloat(plan.monthlyEquivalent.replace(/[^0-9.]/g, '')) || 0;
  const addOnDollars = includeAddOn ? addOn.amount / 100 : 0;
  const total = planDollars + addOnDollars;

  const handlePlaceholderConfirm = async () => {
    if (submitting) return;
    if (!sessionId) {
      // Dev mode: visiting /checkout/:plan directly without an active session.
      if (import.meta.env.DEV) {
        navigate(`/thank-you?plan=${plan.id}`);
        return;
      }
      navigate('/');
      return;
    }
    setSubmitting(true);
    setError(null);
    track('checkout_submitted', {
      plan: plan.id,
      addOn: includeAddOn ? addOn.flavor : null,
    });
    const resp = await postJson<CheckoutResp>('/api/checkout-intent', {
      client_session_id: sessionId,
      plan: plan.id,
      add_on: includeAddOn ? addOn.flavor : null,
      add_on_amount_cents: includeAddOn ? addOn.amount : null,
    });
    if (resp?.ok && resp.redirectUrl) {
      if (resp.redirectUrl.startsWith('http')) {
        window.location.href = resp.redirectUrl;
      } else {
        navigate(resp.redirectUrl);
      }
      return;
    }
    if (resp == null) {
      navigate(`/thank-you?plan=${plan.id}`);
      return;
    }
    setSubmitting(false);
    setError(resp.error ?? 'Could not start checkout. Please try again.');
  };

  return (
    <QuizLayout showBack wide>
      {/* Aspirational header — full width on every breakpoint. Replaces the
          flat "you picked monthly" heading with a stronger reason-to-buy. */}
      <header className="mb-9 text-center sm:text-left">
        <h1 className="text-display-md sm:text-display-lg text-ink leading-[1.05] mb-4 max-w-3xl mx-auto sm:mx-0">
          Take a <em className="not-italic font-extrabold">shortcut</em> to the
          most fulfilling &{' '}
          <em className="not-italic font-extrabold text-brand">profitable</em>{' '}
          years of your career.
        </h1>
        <p className="text-ink-soft text-lg max-w-2xl mx-auto sm:mx-0">
          Invest in yourself and enjoy lifelong career success.
        </p>
        <TrustStrip />
      </header>

      {/* Layout:
          DESKTOP (lg+): two columns. LEFT (col-5): plan recap → next-steps → testimonials.
            RIGHT (col-7): add-on toggle → checkout panel.
          MOBILE: single column with explicit interleaved order — plan, add-on, checkout,
            next-steps, testimonials. The column wrappers use `display: contents` on
            mobile so their children become direct grid items where `order-N` controls
            position; `lg:block` restores them as proper column wrappers on desktop. */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8 lg:items-start">
        <div className="contents lg:block lg:col-span-5 lg:space-y-5">
          <div className="order-1 lg:order-none">
            <PlanRecap plan={plan} />
          </div>
          <div className="order-4 lg:order-none">
            <NextSteps addOnFlavor={addOn.flavor} includeAddOn={includeAddOn} />
          </div>
          <div className="order-5 lg:order-none">
            <SidebarTestimonials variables={variables} />
          </div>
        </div>

        <div className="contents lg:block lg:col-span-7 lg:space-y-5">
          <div className="order-2 lg:order-none">
            <AddOnGate
              addOn={addOn}
              choice={addOnChoice}
              onChoice={(c) => {
                setAddOnChoice(c);
                track('checkout_addon_choice', { plan: plan.id, choice: c, flavor: addOn.flavor });
              }}
            />
          </div>
          <div className="order-3 lg:order-none">
            <StripeCheckoutPanel
              plan={plan}
              addOnCents={includeAddOn ? addOn.amount : 0}
              addOnFlavor={includeAddOn ? addOn.flavor : null}
              totalDollars={total}
              onPlaceholderConfirm={handlePlaceholderConfirm}
              submitting={submitting}
              error={error}
              clientSessionId={sessionId}
            />
          </div>
        </div>
      </div>
    </QuizLayout>
  );
}

// ---------- Plan recap (left column, top) ----------

function PlanRecap({ plan }: { plan: Plan }) {
  return (
    <div className="rounded-3xl border-2 border-ink/10 bg-white p-6 sm:p-7">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <h2 className="text-lg font-extrabold text-ink">{plan.name} plan</h2>
        {plan.savings && (
          <span className="rounded-full bg-teal-100 text-teal-800 text-xs font-bold px-2 py-0.5 flex-shrink-0">
            {plan.savings}
          </span>
        )}
      </div>
      <p className="text-ink-soft text-sm mb-5">{plan.blurb}</p>

      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-display-sm font-extrabold text-ink">
          {plan.monthlyEquivalent}
        </span>
        <span className="text-ink-soft text-sm">{plan.cadence}</span>
      </div>
      <p className="text-sm font-semibold text-ink-soft mb-2">
        Just <span className="text-ink font-bold">{plan.perDay}</span> a day
      </p>
      <p className="text-sm text-ink-light mb-6">{plan.billedTotal}</p>

      <h3 className="text-xs font-bold uppercase tracking-wider text-ink mb-3">
        Includes
      </h3>
      <ul className="space-y-2">
        {CHECKOUT_FEATURES.map((f) => (
          <li key={f.label} className="flex items-center gap-2.5 text-sm text-ink">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0d9d7c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="flex-shrink-0">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span className="font-semibold leading-snug">{f.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------- Add-on gate (left column, top — must be answered before checkout) ----------

function AddOnGate({
  addOn,
  choice,
  onChoice,
}: {
  addOn: AddOn;
  choice: 'yes' | 'no';
  onChoice: (c: 'yes' | 'no') => void;
}) {
  const dollars = (addOn.amount / 100).toFixed(0);
  const coach = addOn.coach;
  return (
    <div className="relative rounded-3xl border-2 border-brand/40 bg-brand-50 p-6 sm:p-7">
      <div className="absolute -top-3 -right-3 bg-brand rounded-xl border-2 border-ink shadow-cta px-3 py-1.5">
        <p className="text-xs font-extrabold text-ink uppercase tracking-wider whitespace-nowrap">Your coach match</p>
      </div>
      <div className="flex items-start gap-4 mb-4 pr-16">
        {coach.photo ? (
          <img src={coach.photo} alt={coach.name} className="w-16 h-16 rounded-full object-cover border-2 border-ink flex-shrink-0" />
        ) : (
          <span className="w-16 h-16 rounded-full bg-ink text-brand font-extrabold text-lg flex items-center justify-center border-2 border-ink flex-shrink-0">{coach.initials}</span>
        )}
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-extrabold text-ink leading-tight">{coach.headline}</h2>
          <p className="text-xs text-ink-soft mt-1">
            {coach.title}{coach.rating ? ` · ★ ${coach.rating.toFixed(2)}` : ''} · {coach.durationLabel} discovery session
          </p>
        </div>
      </div>
      <p className="text-ink text-sm font-semibold leading-relaxed mb-3">{coach.subhead}</p>
      <ul className="space-y-1.5 mb-5">
        {coach.whyLines.map((w) => (
          <li key={w} className="flex items-start gap-2 text-sm text-ink-soft">
            <span className="text-teal-600 font-bold mt-0.5">✓</span><span>{w}</span>
          </li>
        ))}
      </ul>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          aria-pressed={choice === 'yes'}
          onClick={() => onChoice('yes')}
          className={`rounded-2xl border-2 py-4 px-4 text-base font-extrabold transition-all duration-100 ${
            choice === 'yes'
              ? 'border-ink bg-ink text-white shadow-[4px_4px_0_0_rgba(15,18,23,1)]'
              : 'border-ink/15 bg-white text-ink hover:border-ink/40'
          }`}
        >
          {choice === 'yes' ? `Session with ${coach.firstName} added!` : `Book with ${coach.firstName} — $${dollars}`}
        </button>
        <button
          type="button"
          aria-pressed={choice === 'no'}
          onClick={() => onChoice('no')}
          className={`rounded-2xl border-2 py-4 px-4 text-base font-extrabold transition-all duration-100 ${
            choice === 'no'
              ? 'border-ink bg-ink text-white shadow-[4px_4px_0_0_rgba(15,18,23,1)]'
              : 'border-ink/15 bg-white text-ink hover:border-ink/40'
          }`}
        >
          No thanks
        </button>
      </div>
      <p className="text-xs text-ink-soft mt-3">You'll pick a time on {coach.firstName}'s calendar right after checkout.</p>
    </div>
  );
}

// ---------- Add-on derivation from quiz state ----------

const ACTIVE_LOOKING_PURPOSES = new Set(['careerSwitch', 'return', 'lost']);

function deriveAddOn(variables: Record<string, string>, identity: string | null): AddOn {
  const coach = matchCoach(variables, identity);
  const employmentStatus = variables.employmentStatus ?? '';
  const purpose = variables.testTakingPurpose ?? '';
  const isActivelyLooking =
    employmentStatus === 'searching' || ACTIVE_LOOKING_PURPOSES.has(purpose);

  // Salary parsing — currentSalary is freeText; strip non-digits.
  const salaryRaw = (variables.currentSalary ?? '').replace(/[^0-9]/g, '');
  const salary = parseInt(salaryRaw, 10);
  const isHighSalary = Number.isFinite(salary) && salary > 150_000;
  // Pricing tiers mirror the Stripe Price IDs + coupons configured in
  // create-checkout.ts. Duration mirrors the calendar each tier books into:
  //   $49  → 30-min discovery (Erin)
  //   $67  → 60-min discovery (Linda, executive)
  const amountCents = isHighSalary ? 6700 : 4900;
  const durationLabel = isHighSalary ? '60 minutes' : '30 minutes';

  if (isActivelyLooking) {
    return {
      flavor: 'recruiter',
      amount: amountCents,
      coach,
      gateTitle: 'Want a recruiter to walk through your report?',
      gateBlurb:
        `${durationLabel}, 1:1, with a recruiter who places people into roles like your top matches. Bring your résumé — they’ll review it live.`,
    };
  }
  return {
    flavor: 'coach',
    amount: amountCents,
    coach,
    gateTitle: 'Want a coach to walk through your report?',
    gateBlurb:
      `${durationLabel}, 1:1, with a career coach. Map the growth paths in your current role and the 2–3 skill investments with the highest leverage.`,
  };
}

// ---------- Trust strip (header, three reassurance badges) ----------

function TrustStrip() {
  const items: { title: string; body: string; icon: React.ReactNode; tint: string }[] = [
    {
      title: 'Secure',
      body: 'Encrypted',
      tint: 'bg-teal-50 text-teal-600',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <polyline points="9 12 11 14 15 10" />
        </svg>
      ),
    },
    {
      title: 'Guarantee',
      body: '30-day refund',
      tint: 'bg-brand-50 text-brand',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v12M15.5 9.5c-.5-1.5-2-2.5-3.5-2.5s-3 1-3 2.5 1 2 3 2.5 3 1 3 2.5-1.5 2.5-3 2.5-3-1-3.5-2.5" />
        </svg>
      ),
    },
    {
      title: 'Support',
      body: '24/7 chat',
      tint: 'bg-ink/5 text-ink',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5z" />
          <path d="M8 11h.01M12 11h.01M16 11h.01" />
        </svg>
      ),
    },
  ];
  return (
    <div className="mt-8 grid grid-cols-3 gap-2 sm:gap-3">
      {items.map((it) => (
        <div
          key={it.title}
          className="flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-3 rounded-2xl border-2 border-ink/10 bg-white px-2 py-3 sm:px-4 text-center sm:text-left"
        >
          <span className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${it.tint}`}>
            {it.icon}
          </span>
          <div className="min-w-0">
            <p className="text-xs sm:text-sm font-extrabold text-ink leading-tight">{it.title}</p>
            <p className="text-xs text-ink-soft leading-tight mt-0.5 sm:mt-0">{it.body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Next steps (left column) ----------

function NextSteps({
  addOnFlavor,
  includeAddOn,
}: {
  addOnFlavor: 'recruiter' | 'coach';
  includeAddOn: boolean;
}) {
  const flavorLabel = addOnFlavor === 'recruiter' ? 'recruiter' : 'coach';
  const steps: { n: number; title: string; body: string }[] = [
    {
      n: 1,
      title: 'Pay securely',
      body: 'One-click checkout — encrypted, processed by Stripe.',
    },
    {
      n: 2,
      title: 'Dashboard unlocks instantly',
      body: 'Your top 10 matches, personality breakdown, and 90-day plan are waiting.',
    },
    {
      n: 3,
      title: includeAddOn ? `Book your 1:1 with a ${flavorLabel}` : 'Start exploring',
      body: includeAddOn
        ? 'Pick a time from your dashboard — usually within 48 hours.'
        : 'Unlimited retakes, weekly group coaching, and live job alerts are all on.',
    },
  ];
  return (
    <div className="rounded-3xl border-2 border-ink/10 bg-white p-6 sm:p-7">
      <h3 className="text-xs font-bold uppercase tracking-wider text-ink mb-4">
        What happens next
      </h3>
      <ol className="space-y-4">
        {steps.map((s) => (
          <li key={s.n} className="flex items-start gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-ink text-white text-sm font-extrabold flex items-center justify-center">
              {s.n}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-bold text-ink leading-snug">{s.title}</p>
              <p className="text-xs text-ink-soft leading-relaxed mt-0.5">{s.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ---------- Sidebar testimonials (left column) ----------

function SidebarTestimonials({ variables }: { variables: Record<string, string> }) {
  const ranked = useMemo(() => rankReviewsByPersona(REVIEWS, variables), [variables]);
  const picks = ranked.slice(0, 2);
  return (
    <div className="rounded-3xl border-2 border-ink/10 bg-white p-6 sm:p-7">
      <h3 className="text-xs font-bold uppercase tracking-wider text-ink mb-4">
        From people like you
      </h3>
      <div className="space-y-5">
        {picks.map((r) => (
          <SidebarReview key={r.id} review={r} />
        ))}
      </div>
    </div>
  );
}

function SidebarReview({ review }: { review: Review }) {
  // Trim long bodies — sidebar space is tight; ~180 chars feels right.
  const MAX = 180;
  const body =
    review.body.length > MAX ? `${review.body.slice(0, MAX).trimEnd()}…` : review.body;
  return (
    <figure>
      <div className="flex items-center gap-0.5 mb-2" aria-label={`${review.rating} out of 5 stars`}>
        {Array.from({ length: review.rating }).map((_, i) => (
          <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#eb9f2e" aria-hidden>
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        ))}
      </div>
      <blockquote className="text-sm text-ink leading-relaxed mb-2">
        “{body}”
      </blockquote>
      <figcaption className="text-xs text-ink-soft font-semibold">
        {review.author}
        {review.verified && <span className="text-ink-light font-normal"> · Verified</span>}
      </figcaption>
    </figure>
  );
}
